import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useAtom } from 'jotai';
import accessTokenAtom from 'data/atoms/accessTokenAtom';
import { ICompanyViewModel, ThirdPartEnum } from 'Interfaces/ICompaniesConfig';
import { useSnackbar } from 'notistack';
import { useGet } from 'http/useApi';
import { rq } from 'http/apiRoutes';
import { CloudFactoryService } from '../APi/CloudFactoryAPI';
import {
  CloudFactoryCompareProduct,
  removeLastColonPart,
} from '../Converts/ICloudFactoryInterfaces';
import getBearer from 'http/getBearer';
import { EnumBindingPeriod } from 'Interfaces/IGlobalTypeConfig';
import { api } from 'http/apiClient';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const CloudFactoryAllProductsDrawer: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const [accessToken] = useAtom(accessTokenAtom);
  const { enqueueSnackbar } = useSnackbar();

  // Hent alle BLUU-selskaper via useGet
  const { data: companyData, isFetching: fetchingCompanies } = useGet(
    rq.getCompanyByThirdParty(ThirdPartEnum.CloudFactory, true)
  );

  // Endringer per selskap
  const [changes, setChanges] = useState<
    Record<string, CloudFactoryCompareProduct[]>
  >({});
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAll = async () => {
      const companies = (companyData?.companies ??
        []) as ICompanyViewModel[];

      if (!companies.length || !accessToken || !open) return;

      setLoadingProducts(true);
      setError(null);

      const result: Record<string, CloudFactoryCompareProduct[]> = {};

      try {
        for (const comp of companies) {
          try {
            // 1) Hent abonnementer
            const subsResp = await CloudFactoryService.getSubscriptions(
              comp.thirdPartyId,
              accessToken
            );
            const subs = subsResp.data;

            console.log(
              `Funnet ${subs.length} subscriptions for ${comp.name}`,
              subs
            );

            const list: CloudFactoryCompareProduct[] = [];

            // 2) Hent katalog og bygg liste
            for (const sub of subs) {
              try {
                const catResp = await CloudFactoryService.getCatalogueProducts(
                  comp.thirdPartyId,
                  removeLastColonPart(sub.sku),
                  sub.billingCycle,
                  sub.termDuration,
                  1,
                  1,
                  accessToken
                );

                const item = catResp.data.results[0];
                const { price, product } = item;

                // Justér måned / år
                const retailPrice =
                  price.sale /
                  (sub.billingCycle === 'Monthly' ? 1 : 12);
                const unitPrice =
                  price.cost /
                  (sub.billingCycle === 'Monthly' ? 1 : 12);

                list.push({
                  companyId: comp.id,
                  name: product.name,
                  description: product.description,
                  sku: removeLastColonPart(sub.sku),
                  bindingPeriod:
                    sub.termDuration === 'P1Y'
                      ? EnumBindingPeriod.Year
                      : EnumBindingPeriod.Month,
                  paymentCycle:
                    sub.billingCycle === 'Annually'
                      ? EnumBindingPeriod.Year
                      : EnumBindingPeriod.Month,
                  costPrice: unitPrice,
                  unitPrice: retailPrice,
                  quantity: sub.quantity,
                  companyProductQty: 0,
                  commitmentEndDate: sub.commitmentEndDate,
                });
              } catch (innerErr) {
                console.error('Katalog-feil for subscription', sub, innerErr);
                enqueueSnackbar(`Ikke hentet katalog for ${sub.sku}`, {
                  variant: 'warning',
                });
              }
            }

            // 3) Slå sammen like produkter
            const merged: Record<string, CloudFactoryCompareProduct> = {};
            list.forEach((item) => {
              const key = `${item.name}|${item.sku}|${item.bindingPeriod}|${item.paymentCycle}`;
              if (!merged[key]) {
                merged[key] = { ...item };
              } else {
                merged[key].quantity += item.quantity;
              }
            });
            const mergedList = Object.values(merged);

            // 4) Sammenlign mot backend
            const bearer = await getBearer();
            if (!bearer) {
              enqueueSnackbar(
                `Ingen token tilgjengelig for sammenligning av ${comp.name}`,
                { variant: 'error' }
              );
              continue;
            }

            const resp = await api.post<{ cloudFactoryProducts: any[] }>(
              '/Module/CloudFactory/ComparedProducts',
              { cloudFactoryProducts: mergedList },
              {
                headers: {
                  Authorization: `Bearer ${bearer}`,
                },
              }
            );

            const existing = resp.data.cloudFactoryProducts;
            console.log(
              `Sjekket ${existing.length} produkter mot CloudFactory for ${comp.name}`,
              existing
            );

            // 5) Oppdater med DB-verdier
            const compare: CloudFactoryCompareProduct[] = existing.map((p) => ({
              companyId: p.companyId,
              name: p.name,
              description: p.description,
              sku: p.sku,
              bindingPeriod: p.bindingPeriod,
              paymentCycle: p.paymentCycle,
              costPrice: p.costPrice,
              unitPrice: p.unitPrice,
              quantity: p.quantity,
              companyProductQty: p.companyProductQuantity,
              commitmentEndDate: p.product?.commitmentEndDate,
            }));

            enqueueSnackbar(
              `Sjekket ${existing.length} produkter mot database for ${comp.name}`,
              { variant: 'info' }
            );

            result[comp.name] = compare;
          } catch (err: any) {
            console.error('Feil for selskap', comp.name, err);
            enqueueSnackbar(`Feil for ${comp.name}: ${err.message}`, {
              variant: 'error',
            });
          }
        }

        setChanges(result);
      } catch (outer: any) {
        console.error('Global feil i CloudFactoryAllProductsDrawer', outer);
        setError(outer.message || 'Ukjent feil ved henting av produkter');
      } finally {
        setLoadingProducts(false);
      }
    };

    loadAll();
  }, [companyData, accessToken, enqueueSnackbar, open]);

  return (
    <Drawer
      anchor='left'
      open={open}
      variant='persistent'
      PaperProps={{ sx: { width: '100%', height: '100%' } }}
      onClose={onClose}
    >
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 20 }}>
          Alle Produkter Endringer
        </Typography>
        <IconButton onClick={onClose}>
          <CancelRoundedIcon />
        </IconButton>
      </Box>

      {(fetchingCompanies || loadingProducts) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color='error' sx={{ p: 2 }}>
          {error}
        </Typography>
      )}

      {!loadingProducts && companyData?.companies && (
        <Box sx={{ overflowY: 'auto', height: `calc(100% - 64px)` }}>
          {(companyData.companies as ICompanyViewModel[]).map(
            (comp: ICompanyViewModel) => (
              <Accordion key={comp.id} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {comp.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {(changes[comp.name] || []).map((item) => (
                      <Grid item xs={12} key={item.sku}>
                        <Box
                          sx={{ p: 1, border: '1px solid', borderRadius: 1 }}
                        >
                          <Typography>{item.name}</Typography>
                          <Typography variant='caption'>
                            KVANTITET: {item.quantity} ➔ DB:{' '}
                            {item.companyProductQty}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )
          )}
        </Box>
      )}

      {companyData?.companies?.length === 0 && !fetchingCompanies && (
        <Typography sx={{ p: 2 }}>Ingen selskaper funnet.</Typography>
      )}
    </Drawer>
  );
};
